import { useEffect, useRef, useState } from 'react';
import CyberLunaWindow from '../base/CyberLunaWindow';
import styles from './EventsPreview.module.css';
import content from '../../data/content.json';

const { events } = content.global;

/**
 * EventsPreview Component - Shotgun widget integration
 *
 * @component
 * @description
 * Displays the Shotgun.live events widget for The Boombox Miami inside the
 * XP "Luna" window chrome (CyberLunaWindow), so the embed matches the rest of
 * the desktop metaphor. Lazy-loads when in viewport for performance.
 *
 * @param {string} [windowTitle] - Title-bar caption; defaults to the shared copy.
 *
 * @example
 * <EventsPreview />
 */
const EventsPreview = ({ windowTitle = events.window_title }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const sectionRef = useRef(null);
  const iframeRef = useRef(null);

  // Lazy load when section enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load Shotgun widget script when iframe is visible
  useEffect(() => {
    if (isVisible && !isLoaded) {
      // Check if script is already loaded
      const existingScript = document.querySelector('script[src="https://shotgun.live/widget.js"]');

      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://shotgun.live/widget.js';
        script.async = true;
        document.body.appendChild(script);
      }

      setIsLoaded(true);
    }
  }, [isVisible, isLoaded]);

  return (
    <section ref={sectionRef} className={styles.eventsSection}>
      <div className={styles.container}>
        <CyberLunaWindow
          isStatic
          title={windowTitle}
          className={styles.eventsWindow}
          bodyClassName={styles.windowBody}
          maxBodyClassName={styles.windowMaxBody}
        >
          {/* Shotgun Widget — heights live in the stylesheet so the maximized
              modal can override them without fighting inline styles. */}
          {isVisible ? (
            <div className={styles.shotgunWidget}>
              <iframe
                ref={iframeRef}
                src="https://shotgun.live/venues/the-boombox-miami?embedded=1&ui=dark"
                allow="payment"
                className={styles.shotgunIframe}
                title="The Boombox Miami Events on Shotgun"
                loading="lazy"
              />
            </div>
          ) : (
            <div className={styles.loadingState}>
              <div className={styles.loader}></div>
              <p>Loading events...</p>
            </div>
          )}
        </CyberLunaWindow>
      </div>
    </section>
  );
};

export default EventsPreview;
